(() => {
  if (window.__burdlifeAccessibilityLoaded) return;
  window.__burdlifeAccessibilityLoaded = true;

  const focusableSelector = 'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';
  const storedTabindex = new Map();
  const observedCarousels = new WeakSet();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const text = (element) => element && element.textContent ? element.textContent.replace(/\s+/g, ' ').trim() : '';

  const productNameFor = (element) => {
    const card = element.closest('.boost-sd__product-item, [data-product-card], [data-grid-item]');
    const title = card ? card.querySelector('.boost-sd__product-title, .boost-sd__product-link, .product-item__title') : null;
    const image = element.querySelector('img');
    return text(title) ||
      (image && image.getAttribute('alt') ? image.getAttribute('alt').trim() : '') ||
      'product';
  };

  const improveAppContent = (root = document) => {
    if (!root.querySelectorAll) return;
    root.querySelectorAll('.boost-sd__product-link-image').forEach((link) => {
      const productName = productNameFor(link);
      if (!link.getAttribute('aria-label') && !text(link)) {
        link.setAttribute('aria-label', `View ${productName}`);
      }
      link.querySelectorAll('img').forEach((image) => {
        if (!image.getAttribute('alt') || !image.getAttribute('alt').trim()) image.setAttribute('alt', productName);
      });
    });

    root.querySelectorAll('.boost-sd__search-widget-init-input, .boost-sd__search-form input[type="search"], input[type="search"]').forEach((input) => {
      if (!input.getAttribute('aria-label') && !input.getAttribute('aria-labelledby') && (!input.labels || !input.labels.length)) {
        input.setAttribute('aria-label', 'Search products');
      }
    });

    root.querySelectorAll('.boost-sd__search-widget-init-button, .boost-sd__search-form button[type="submit"], form[action*="/search"] button[type="submit"]').forEach((button) => {
      if (!button.getAttribute('aria-label') && !text(button)) button.setAttribute('aria-label', 'Submit search');
    });

    root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      if (text(heading).toUpperCase() === 'GENERAL TITLE (WHEN NO SEARCH TERM)') heading.textContent = 'Search';
    });
  };

  const ensureHeading = () => {
    const fallback = document.querySelector('[data-burdlife-fallback-h1]');
    const realHeading = Array.from(document.querySelectorAll('h1')).find((heading) => !heading.hasAttribute('data-burdlife-fallback-h1'));
    if (realHeading) {
      if (fallback) fallback.remove();
      return;
    }
    if (fallback) return;
    const heading = document.createElement('h1');
    heading.className = 'visually-hidden';
    heading.setAttribute('data-burdlife-fallback-h1', '');
    const siteName = document.querySelector('meta[property="og:site_name"]');
    heading.textContent = document.body.classList.contains('template-index')
      ? (siteName && siteName.content ? siteName.content : 'BURDLIFE')
      : document.title.split(/[|–-]/)[0].trim() || 'BURDLIFE';
    const main = document.querySelector('#MainContent');
    if (main) main.prepend(heading);
  };

  const syncDesktopNavigation = () => {
    document.querySelectorAll('[data-hover-disclosure-toggle]').forEach((wrapper) => {
      const link = wrapper.querySelector(':scope > [data-top-link]');
      if (!link) return;
      wrapper.removeAttribute('role');
      const controls = wrapper.getAttribute('aria-controls') || wrapper.getAttribute('data-hover-disclosure-toggle');
      const expanded = wrapper.getAttribute('aria-expanded') || 'false';
      wrapper.removeAttribute('aria-haspopup');
      wrapper.removeAttribute('aria-controls');
      if (link.getAttribute('aria-haspopup') !== 'true') link.setAttribute('aria-haspopup', 'true');
      if (link.getAttribute('aria-controls') !== controls) link.setAttribute('aria-controls', controls);
      if (link.getAttribute('aria-expanded') !== expanded) link.setAttribute('aria-expanded', expanded);
    });
  };

  const sliderOptions = (slider) => {
    const value = slider.getAttribute('data-options');
    if (!value) return {};

    try {
      return JSON.parse(decodeURIComponent(value));
    } catch {
      return {};
    }
  };

  const stopAutomaticMotion = (root = document) => {
    if (!reducedMotion.matches || !root.querySelectorAll) return;

    for (const slider of root.querySelectorAll('[data-slider], [data-slider-mobile]')) {
      const options = sliderOptions(slider);
      if (options.autoPlay) {
        options.autoPlay = false;
        slider.setAttribute('data-options', JSON.stringify(options));
      }

      const flickity = window.themeVendor?.Flickity?.data?.(slider);
      if (flickity) {
        flickity.options.autoPlay = false;
        flickity.stopPlayer();
      }
    }

    for (const ticker of root.querySelectorAll('ticker-bar[autoplay]')) {
      ticker.removeAttribute('autoplay');
      ticker.setAttribute('paused', '');
    }
  };

  const carouselLabel = (slider) => {
    const section = slider.closest('section, [data-section-id], announcement-bar');
    const heading = section?.querySelector('h1, h2, h3, .section__heading, .grid__heading');
    const headingText = text(heading);
    if (headingText) return `${headingText} carousel`;
    if (slider.closest('announcement-bar')) return 'Store announcements carousel';
    return 'Featured content carousel';
  };

  const syncCarousel = (slider) => {
    const slides = Array.from(slider.querySelectorAll(':scope > [data-slide]:not(.is-cloned)'));
    if (slides.length <= 1) return;

    if (!slider.id) slider.id = `BurdlifeCarousel-${Math.random().toString(36).slice(2, 10)}`;
    slider.setAttribute('role', 'region');
    slider.setAttribute('aria-roledescription', 'carousel');
    if (!slider.getAttribute('aria-label') && !slider.getAttribute('aria-labelledby')) {
      slider.setAttribute('aria-label', carouselLabel(slider));
    }

    const options = sliderOptions(slider);
    slider.setAttribute('aria-live', options.autoPlay && !reducedMotion.matches ? 'off' : 'polite');

    for (const [index, slide] of slides.entries()) {
      const isSelected = slide.classList.contains('is-selected') || (!slider.classList.contains('flickity-enabled') && index === 0);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Slide ${index + 1} of ${slides.length}`);
      slide.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
    }

    for (const clone of slider.querySelectorAll(':scope > .is-cloned')) {
      clone.setAttribute('aria-hidden', 'true');
    }

    for (const control of slider.querySelectorAll('.flickity-button, [data-button-arrow]')) {
      control.setAttribute('aria-controls', slider.id);
    }
  };

  const improveCarousels = (root = document) => {
    if (!root.querySelectorAll) return;

    for (const slider of root.querySelectorAll('[data-slider], [data-slider-mobile]')) {
      syncCarousel(slider);
      if (observedCarousels.has(slider)) continue;

      observedCarousels.add(slider);
      new MutationObserver(() => syncCarousel(slider)).observe(slider, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
  };

  const addressOpeners = new Map();

  const improveAddressToggles = (root = document) => {
    if (!root.querySelectorAll) return;

    for (const button of root.querySelectorAll('.address-new-toggle, .address-edit-toggle')) {
      const controlId = button.getAttribute('aria-controls');
      const panel = controlId ? document.getElementById(controlId) : null;
      if (!panel) continue;

      button.setAttribute('aria-expanded', panel.classList.contains('hidden') ? 'false' : 'true');
      if (button.dataset.burdlifeAddressToggle === 'true') continue;
      button.dataset.burdlifeAddressToggle = 'true';

      button.addEventListener('click', () => {
        window.requestAnimationFrame(() => {
          const isOpen = !panel.classList.contains('hidden');
          for (const relatedButton of document.querySelectorAll(`[aria-controls="${controlId}"]`)) {
            relatedButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
          }

          if (isOpen) {
            addressOpeners.set(controlId, button);
            panel.querySelector('input:not([type="hidden"]), select, textarea, button')?.focus();
            return;
          }

          const opener = addressOpeners.get(controlId);
          if (opener && opener !== button && opener.isConnected) opener.focus();
          addressOpeners.delete(controlId);
        });
      });
    }
  };

  const setMenuIsolation = (open, drawer) => {
    document.body.classList.toggle('burdlife-menu-open', open);
    if (open) {
      document.querySelectorAll(focusableSelector).forEach((element) => {
        if (drawer.contains(element)) return;
        storedTabindex.set(element, element.getAttribute('tabindex'));
        element.setAttribute('tabindex', '-1');
      });
      ['#MainContent', '.footer-sections', '.bl-assistant', '.bl-new-member-tab'].forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => element.setAttribute('aria-hidden', 'true'));
      });
      return;
    }

    storedTabindex.forEach((value, element) => {
      if (!element.isConnected) return;
      if (value === null) element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', value);
    });
    storedTabindex.clear();
    ['#MainContent', '.footer-sections', '.bl-assistant', '.bl-new-member-tab'].forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => element.removeAttribute('aria-hidden'));
    });
  };

  const init = () => {
    stopAutomaticMotion();
    improveAppContent();
    ensureHeading();
    syncDesktopNavigation();
    improveCarousels();
    improveAddressToggles();

    document.addEventListener('theme:slider:loaded', (event) => {
      stopAutomaticMotion(event.target);
      improveCarousels(event.target);
    });

    reducedMotion.addEventListener('change', () => {
      stopAutomaticMotion();
      improveCarousels();
    });

    const drawer = document.querySelector('.drawer--header');
    let menuOpen = drawer ? drawer.classList.contains('is-open') : false;
    if (drawer) {
      setMenuIsolation(menuOpen, drawer);
      new MutationObserver(() => {
        const nextOpen = drawer.classList.contains('is-open');
        if (nextOpen !== menuOpen) {
          menuOpen = nextOpen;
          setMenuIsolation(menuOpen, drawer);
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        improveAppContent();
        ensureHeading();
        syncDesktopNavigation();
        stopAutomaticMotion();
        improveCarousels();
        improveAddressToggles();
      });
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
